import { Colors, type ColorName } from "main";

/**
 * Foundation | Typography
 *
 * Provides consistent typographic styles across the app, aligned with the design system.
 * Each exported function returns an Emotion style object that can be applied to any element.
 * Font size, weight, color, and style can be customized via optional params.
 *
 * - Defaults to `inherit` for color and fontFamily
 * - Variant options: xSmall, small, medium, large, xLarge, xxLarge
 *
 * @Figma https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=685-18735&p=f
 */

export type TypographyVariantParams = {
  xSmall: {
    color?: ColorName;
    fontWeight?: 400 | 600;
  };
  small: { color?: ColorName; fontWeight?: 400 | 600 };
  medium: {
    color?: ColorName;
    fontWeight?: 400 | 600;
    fontStyle?: "normal" | "italic";
    isStrikethrough?: boolean;
  };
  large: { color?: ColorName; fontWeight?: 400 | 500 | 600 };
  xLarge: { color?: ColorName };
  xxLarge: { color?: ColorName };
};

export const fontXSmall = (params?: TypographyVariantParams["xSmall"]) => ({
  color: params?.color ? Colors[params.color] : "inherit",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: params?.fontWeight ?? 400,
});

export const fontSmall = (params?: TypographyVariantParams["small"]) => ({
  color: params?.color ? Colors[params.color] : "inherit",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: params?.fontWeight ?? 400,
});

export const fontMedium = (params?: TypographyVariantParams["medium"]) => ({
  color: params?.color ? Colors[params.color] : "inherit",
  fontFamily: "inherit",
  fontSize: 16,
  fontStyle: params?.fontStyle ?? "normal",
  fontWeight: params?.fontWeight ?? 400,
  textDecoration: params?.isStrikethrough ? "line-through" : "none",
});

export const fontLarge = (params?: TypographyVariantParams["large"]) => ({
  color: params?.color ? Colors[params.color] : "inherit",
  fontFamily: "inherit",
  fontSize: 20,
  fontWeight: params?.fontWeight ?? 400,
});

export const fontXLarge = (params?: TypographyVariantParams["xLarge"]) => ({
  color: params?.color ? Colors[params.color] : "inherit",
  fontFamily: "inherit",
  fontSize: 24,
  fontWeight: 500,
});

export const fontXXLarge = (params?: TypographyVariantParams["xxLarge"]) => ({
  color: params?.color ? Colors[params.color] : "inherit",
  fontFamily: "inherit",
  fontSize: 30,
  fontWeight: 500,
});

/**
 * EXAMPLE USAGE
 *
 * <h1 css={{ ...fontXXLarge() }}>Title</h1>
 * <p css={{ ...fontMedium({ fontStyle: "italic" }), margin: 0 }}>main content</p>
 * <span css={{ ...fontSmall({ fontWeight: 600 }), }}>inline text</span>
 */
